import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'

import { DoubleTextListGroupLabel, TextListGroupContainer } from './style'
import { useNewTranslation } from 'hooks'
import { reorderList } from 'components/utils'
import RectangleButton from 'particles/Button/RectangleButton'
import Label, { LabelSize } from 'particles/Label'
import { generateUuid } from 'services/uuid'
import { TextListProps } from '.'
import Text from 'particles/Text'
import CommentContainer from 'components/Shared/Comment'
import RichInfoList, { InputProps, NumberInputPropsWithRounding } from './RichInfoList'
import Tooltip from 'particles/Tooltip'
import { FormikTextListGroupProps } from './Group'
import _ from 'lodash'
import { DropdownProps } from 'particles/Dropdown'
import { TextFieldProps } from '../TextField'

export type RichInfoItemValue = { [key: string]: string | undefined }
export type RichInfoListGroupValue = { uuid: string; value: RichInfoItemValue } | undefined

interface DoubleTextListGroupProps extends Omit<FormikDoubleTextListGroupProps, 'onRemoveItem'> {
    onAddItem: (amount: number) => void
    onChange?: (value: RichInfoItemValue, index: number) => void
    onDeleteItem?: (index: number) => void
    onDragEnd: (result: DropResult) => void
    onCommentClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    fieldValue: RichInfoListGroupValue[]
    error?: string
}

const RichInfoListGroup: React.FC<DoubleTextListGroupProps> = ({
    title,
    subtitle,
    mainColumnLabel = '',
    secondaryColumnLabel = '',
    width,
    className,
    isReadOnly,
    isRequired,
    link,
    size = 'm',
    tooltip,
    identifier,
    onDragEnd,
    fieldValue,
    onDeleteItem,
    onAddItem,
    onChange,
    onCommentClick,
    showError,
    isDefault,
    isCommentable = false,
    isCommented = false,
    dataTestId,
    isDisabled = false,
    isDraggable = true,
    maxItem,
    error,
    buttonText,
    mainInputData,
    maxWidth = '600px',
    secondaryInputData,
    itemTooltip,
    valueKeys,
    hideAddButton = false,
    labelSize,
    fixedItemAmount,
}) => {
    const [t] = useNewTranslation()
    const [isLabelOverflow, setIsLabelOverflow] = useState<Record<string, true>>({})
    const showAddButton = !fixedItemAmount && !isReadOnly && !hideAddButton
    const isItemDeletable = !fixedItemAmount && (!isDefault || fieldValue.length > 1)

    // to display tooltip only when label overflow
    const onLabelHover = (isOverflow: boolean, id: 'main' | 'secondary') => {
        const label: Record<string, true> = {
            ...isLabelOverflow,
        }
        if (isOverflow) {
            label[id] = true
        } else {
            delete label[id]
        }
        setIsLabelOverflow(label)
    }

    // give padding to match position right above input list
    // padding define base on props
    const columnTitlePadding = (() => {
        const padding = {
            pl: 4,
            pr: 4,
        }

        const widthBasedOnSize = {
            s: {
                commentIcon: 20,
                dragIcon: 24,
                closeIcon: 24,
                tooltipIcon: 24,
            },
            m: {
                commentIcon: 24,
                dragIcon: 24,
                closeIcon: 32,
                tooltipIcon: 24,
            },
        }[size]

        // when there is drag icon
        const showDragIcon = !isReadOnly && isDraggable && !isDisabled
        if (showDragIcon) {
            padding.pl += widthBasedOnSize.dragIcon
        }

        // when there is comment
        const showCommentIcon = isCommentable && !isDisabled
        if (showCommentIcon) {
            padding.pr += widthBasedOnSize.commentIcon
        }

        // when there is x button
        // isDefault will hide close icon only when item length is 1
        const showCloseIcon = !isDisabled && (!isDefault || (isDefault && fieldValue.length !== 1)) && !isReadOnly
        if (showCloseIcon) {
            padding.pr += widthBasedOnSize.closeIcon
        }

        // when there is tooltip
        if (isDisabled) {
            padding.pr += widthBasedOnSize.tooltipIcon
        }

        return padding
    })()

    const defaultValue = {
        [valueKeys[0]]: undefined,
        [valueKeys[1]]: undefined,
    }

    return (
        <TextListGroupContainer className={className} width={width} maxWidth={maxWidth}>
            {(title || subtitle) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={title || ''}
                        subtext={subtitle}
                        isRequired={isRequired}
                        size={labelSize ? labelSize : size}
                        link={link}
                        tooltip={tooltip}
                    />
                </CommentContainer>
            )}

            <div className="list-item-field">
                {!!fieldValue.length ? (
                    <DoubleTextListGroupLabel
                        pr={columnTitlePadding.pr}
                        maxWidth={maxWidth}
                        pl={columnTitlePadding.pl}
                        mainWidthRatio={mainInputData.widthRatio || '75%'}
                        secondaryWidthRatio={secondaryInputData?.widthRatio || '25%'}
                    >
                        <div className="main-column-label">
                            <Tooltip label={isLabelOverflow['main'] ? mainColumnLabel : undefined} describeChild>
                                <Text
                                    className="label"
                                    scale="bodyS500"
                                    onMouseEnter={e =>
                                        onLabelHover(e.currentTarget.scrollWidth > e.currentTarget.offsetWidth, 'main')
                                    }
                                >
                                    {mainColumnLabel}
                                </Text>
                            </Tooltip>
                        </div>

                        {!!secondaryInputData && (
                            <div className="secondary-column-label">
                                <Tooltip label={isLabelOverflow['secondary'] ? secondaryColumnLabel : undefined}>
                                    <Text
                                        className="label"
                                        scale="bodyS500"
                                        onMouseEnter={e =>
                                            onLabelHover(
                                                e.currentTarget.scrollWidth > e.currentTarget.offsetWidth,
                                                'secondary',
                                            )
                                        }
                                    >
                                        {secondaryColumnLabel}
                                    </Text>
                                </Tooltip>
                            </div>
                        )}
                    </DoubleTextListGroupLabel>
                ) : null}
                <DragDropContext
                    onDragEnd={(result): void => {
                        onDragEnd?.(result)
                    }}
                >
                    <Droppable droppableId="droppable">
                        {(provided): React.ReactElement => (
                            <div className="draggable-group" ref={provided.innerRef} {...provided.droppableProps}>
                                {fieldValue.map((value: RichInfoListGroupValue, index: number) => {
                                    const mainInputDataValue = _.cloneDeep(mainInputData)
                                    const secondaryInputDataValue =
                                        !!secondaryInputData && _.cloneDeep(secondaryInputData)

                                    if (Array.isArray(mainInputData.props.placeholder)) {
                                        mainInputDataValue.props.placeholder = mainInputData.props.placeholder[index]
                                    }
                                    if (
                                        secondaryInputDataValue &&
                                        Array.isArray(secondaryInputData?.props.placeholder)
                                    ) {
                                        secondaryInputDataValue.props.placeholder =
                                            secondaryInputData.props.placeholder?.[index]
                                    }
                                    return (
                                        <Draggable
                                            key={value?.uuid || index}
                                            index={index}
                                            draggableId={value?.uuid || index.toString()}
                                            isDragDisabled={!isDraggable}
                                        >
                                            {(draggableProvided, snapshot): JSX.Element => (
                                                <CommentContainer identifier={`${value?.uuid}_${identifier}`}>
                                                    <RichInfoList
                                                        onDelete={e => {
                                                            e.preventDefault()
                                                            onDeleteItem?.(index)
                                                        }}
                                                        value={value?.value ?? defaultValue}
                                                        onChange={inputValue => {
                                                            onChange?.(inputValue, index)
                                                        }}
                                                        valueKeys={valueKeys}
                                                        hasError={
                                                            showError !== undefined ? showError && !!error : !!error
                                                        }
                                                        tooltipContext={itemTooltip}
                                                        mainInputData={mainInputDataValue as InputProps}
                                                        secondaryInputData={secondaryInputDataValue as InputProps}
                                                        onCommentClick={onCommentClick}
                                                        isDeletable={isItemDeletable}
                                                        isReadOnly={isReadOnly}
                                                        isDraggable={isDraggable && !isDisabled}
                                                        ref={draggableProvided.innerRef}
                                                        draggableProvided={draggableProvided}
                                                        snapshot={snapshot}
                                                        size={size}
                                                        isCommentable={isCommentable}
                                                        isCommented={isCommented}
                                                        isDisabled={isDisabled}
                                                        dataTestId={dataTestId}
                                                        {...draggableProvided.draggableProps}
                                                    />
                                                </CommentContainer>
                                            )}
                                        </Draggable>
                                    )
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {!isDisabled && (showError ?? !!error) ? (
                    <Text color="error" scale="caption400">
                        {error}
                    </Text>
                ) : null}
            </div>
            {showAddButton && (
                <RectangleButton
                    form="outlined"
                    size="s"
                    label={buttonText ? buttonText : t('button.addDo')}
                    data-testid={`test-button-add-${name}`}
                    icon="plus"
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        e.preventDefault()
                        onAddItem && onAddItem(1)
                    }}
                    className="width-fit-content"
                    isDisabled={isDisabled || (!!maxItem && fieldValue.length >= maxItem)}
                />
            )}
        </TextListGroupContainer>
    )
}

export default RichInfoListGroup

// replacing placeholder type to string | string[] without interfering type based props
export interface RichInfoInputProps extends Omit<InputProps, 'props'> {
    props: {
        placeholder: string | string[]
    } & (InputProps extends { type: infer T }
        ? T extends 'number'
            ? Omit<NumberInputPropsWithRounding, 'placeholder'>
            : T extends 'dropdown'
              ? Omit<DropdownProps, 'placeholder'>
              : T extends 'text'
                ? Omit<Partial<TextFieldProps>, 'placeholder'>
                : never
        : never)
}

interface FormikDoubleTextListGroupProps
    extends Omit<FormikTextListGroupProps, 'placeholder' | 'inputIcon' | 'labelSize' | 'textFieldSize'> {
    mainColumnLabel: string
    secondaryColumnLabel?: string
    mainInputData: RichInfoInputProps
    secondaryInputData?: RichInfoInputProps
    maxWidth?: string
    // we will disable only add button when reach max item amount
    maxItem?: number
    // provide a fix amount of item, no add, no delete
    fixedItemAmount?: number
    itemTooltip?: TextListProps['tooltipContext']
    size?: 'm' | 's'
    hideAddButton?: boolean
    onRemoveItem?: (newItem: RichInfoListGroupValue[]) => void
    valueKeys: [string, string]
    labelSize?: LabelSize
}

const FormikRichInfoListGroup: React.FC<FormikDoubleTextListGroupProps> = ({
    mainInputData,
    secondaryInputData,
    name = '',
    title,
    subtitle,
    isDefault = false,
    identifier,
    onRemoveItem,
    fixedItemAmount,
    valueKeys,
    ...props
}) => {
    const [field, meta] = useField<RichInfoListGroupValue[]>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const fieldValue: RichInfoListGroupValue[] = useMemo(() => {
        return field.value
            ? field.value
            : isDefault
              ? [
                    {
                        uuid: generateUuid(),
                        value: {
                            [valueKeys[0]]: undefined,
                            [valueKeys[1]]: undefined,
                        },
                    },
                ]
              : [undefined]
    }, [field.value, isDefault])

    useEffect(() => {
        if (fixedItemAmount && fieldValue.length < fixedItemAmount) {
            handleAddItem(fixedItemAmount - fieldValue.length)
            return
        }
        if (fieldValue.length === 0) {
            handleAddItem(1)
        }
    }, [fieldValue, fixedItemAmount])

    const handleChange = ({ newValue, index }: { newValue: RichInfoItemValue; index: number }) => {
        setFieldValue(`${name}[${index}]`, {
            uuid: fieldValue[index]?.uuid,
            value: newValue,
        })
    }

    const handleAddItem = useCallback(
        (amount: number) => {
            setFieldValue(name, [
                ...fieldValue,
                ...new Array(amount).fill(1).map(() => ({
                    uuid: generateUuid(),
                    value: {
                        [valueKeys[0]]: '',
                        [valueKeys[1]]: '',
                    },
                })),
            ])
        },
        [fieldValue, name, setFieldValue],
    )

    const handleRemoveItem = useCallback(
        index => {
            const newItems = fieldValue.filter((_, i) => i !== index)
            setFieldValue(name, newItems)
            onRemoveItem?.(newItems)
            if (newItems.length === 0) setFieldTouched(field.name, true, false)
        },
        [fieldValue, name, setFieldValue],
    )

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) return

            const items = reorderList([...fieldValue], result.source.index, result.destination.index)

            setFieldValue(field.name, items)
        },
        [fieldValue],
    )

    return (
        <RichInfoListGroup
            {...props}
            title={title}
            valueKeys={valueKeys}
            subtitle={subtitle}
            fieldValue={fieldValue}
            onAddItem={handleAddItem}
            fixedItemAmount={fixedItemAmount}
            onDeleteItem={handleRemoveItem}
            onChange={(value, index) => handleChange({ newValue: value, index })}
            onDragEnd={handleDragEnd}
            error={meta.error}
            identifier={identifier}
            isDefault={isDefault}
            mainInputData={mainInputData}
            secondaryInputData={secondaryInputData}
        />
    )
}

export { FormikRichInfoListGroup }
